/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { CpdsImageStyleTag, CpdsImagWrapper } from './styles/cpds-image-style';
import {
  CpdsAspectRatioTypes,
  CpdsBorderRadiusSizesTypes,
  CpdsImageObjectType,
  CpdsImageProps,
  CpdsUnitType,
} from './typings';
import { checkValue, onImageLoad as imageLoad } from 'components/common/helper';
import {
  CpdsAspectRatioType,
  CpdsObjectFitType,
  extractAspectRatio,
  extractBorderRadius,
} from 'components/common/cpds/cpds-image/constants';

const deviceSizes: Record<string, string> = {
  mobile: '575',
  tablet: '1024',
  desktop: '1200',
  'large-desktop': '1600',
};

export const aspectRatioMap: Record<CpdsAspectRatioType, string> = {
  [CpdsAspectRatioType.CPDS_16x9]: '16/9',
  [CpdsAspectRatioType.CPDS_5x4]: '5/4',
  [CpdsAspectRatioType.CPDS_4x3]: '4/3',
  [CpdsAspectRatioType.CPDS_3x2]: '3/2',
  [CpdsAspectRatioType.CPDS_1x1]: '1/1',
  [CpdsAspectRatioType.CPDS_2x3]: '2/3',
  [CpdsAspectRatioType.CPDS_3x4]: '3/4',
  [CpdsAspectRatioType.CPDS_4x5]: '4/5',
  [CpdsAspectRatioType.CPDS_9x16]: '9/16',
};

export const aspectRatioReverseMap: Record<string, CpdsAspectRatioType> = {
  '16:9': CpdsAspectRatioType.CPDS_16x9,
  '5:4': CpdsAspectRatioType.CPDS_5x4,
  '4:3': CpdsAspectRatioType.CPDS_4x3,
  '3:2': CpdsAspectRatioType.CPDS_3x2,
  '1:1': CpdsAspectRatioType.CPDS_1x1,
  '2:3': CpdsAspectRatioType.CPDS_2x3,
  '3:4': CpdsAspectRatioType.CPDS_3x4,
  '4:5': CpdsAspectRatioType.CPDS_4x5,
  '9:16': CpdsAspectRatioType.CPDS_9x16,
};

const objectFitMap: Record<CpdsObjectFitType, string> = {
  [CpdsObjectFitType.CPDS_FILL]: 'fill',
  [CpdsObjectFitType.CPDS_CONTAIN]: 'contain',
  [CpdsObjectFitType.CPDS_COVER]: 'cover',
  [CpdsObjectFitType.CPDS_NONE]: 'none',
  [CpdsObjectFitType.CPDS_SCALE_DOWN]: 'scale-down',
  [CpdsObjectFitType.CPDS_UNSET]: 'unset',
};

const borderRadiusMap: Record<CpdsBorderRadiusSizesTypes, string> = {
  CPDS_0px: '0px',
  CPDS_4px: '4px',
  CPDS_8px: '8px',
  CPDS_12px: '12px',
  CPDS_16px: '16px',
  CPDS_20px: '20px',
  CPDS_ROUNDED: '50%',
};

const placeholderSrcDefault =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAUoAAAE4CAMAAAAKHUr9AAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAylpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDkuMS1jMDAxIDc5LjE0NjI4OTk3NzcsIDIwMjMvMDYvMjUtMjM6NTc6MTQgICAgICAgICI+IDxyZGY6UkRGIHhtbG5zOnJkZj0iaHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyI+IDxyZGY6RGVzY3JpcHRpb24gcmRmOmFib3V0PSIiIHhtbG5zOnhtcD0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wLyIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bXA6Q3JlYXRvclRvb2w9IkFkb2JlIFBob3Rvc2hvcCAyNS4zIChNYWNpbnRvc2gpIiB4bXBNTTpJbnN0YW5jZUlEPSJ4bXAuaWlkOjM4RDdGNzc4QTM3MzExRUU5QTM3OTg2MENBRDZGNTEyIiB4bXBNTTpEb2N1bWVudElEPSJ4bXAuZGlkOjM4RDdGNzc5QTM3MzExRUU5QTM3OTg2MENBRDZGNTEyIj4gPHhtcE1NOkRlcml2ZWRGcm9tIHN0UmVmOmluc3RhbmNlSUQ9InhtcC5paWQ6M0U2NUU0N0ZBMzQ1MTFFRTlBMzc5ODYwQ0FENkY1MTIiIHN0UmVmOmRvY3VtZW50SUQ9InhtcC5kaWQ6M0U2NUU0ODBBMzQ1MTFFRTlBMzc5ODYwQ0FENkY1MTIiLz4gPC9yZGY6RGVzY3JpcHRpb24+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+IDw/eHBhY2tldCBlbmQ9InIiPz7zDl1TAAAAYFBMVEXHzdXW2+HP1dvU2d/M0tnc4OXL0djFzNTZ3ePIz9bb3+TQ1tzO09rT2d/J0NfY3OLQ1t3R1t3IztbT2d7X3OLR1tzN09rY3eLZ3uPS197T2N7Jz9fS193N1NrS2N7S2N2VsKl5AAAXc0lEQVR42uydi5aiuhKGE5QA4aJ4QbF19vu/5SE3CGrbqSp67J6TWmvP9OzVxOQjVSkw9Yel95YXWdFkgxW5+tnZ+syzjG3SIBOZUH+1mWcsyYIbqNWvsuGPLsuOK335h5ya40GN5I1MpfrlkxpMM17HWXYMub7Jnljx+e+z9BlLbYeH1kJJplx3u727dhveQG0uqodr9MW8yD4S11QZ2Igc/ktUR/54t+BUhbawe4YyA6GUFuAwD04MRdJMy/bh2j68gdox68zFnn+UKcS2XAPVJlP+oZqr07+EcpyKdywBJNW0XCPvgu/jh1R7d+X5CpCkPzmGoKOmpMjDrlqTUcrp/s9YwmioyZTwmmFJWpZH9UemY5tliSeZDbGfZeIMmA7UWZk/ZQmksdKTIO3RJMd4yTZby5KRSCq3blX4BUQpKsqnLKE0hlt6c3cWR9KyVBcbli2OpHFUluS6NwXgQo8DFuUTlmAaKnPKzTCwJMd4qSEesST3aumW2lOYcnIIy46M8oElnIaKuMWakUg6lm2GX3GMmx7UmKoM5N4GRH9T1glrzQ6K8p4lgsbYwodMaSwzEkmd0wwzUoRnQUhjaQhLjC3OEkdSoexMpBF5+g6UHkush44trNMlWCJJqvR8yIIGkDxN34NyJIGPdXmzy4jBcmJZonths6DvJvkCpWVJA7Elk/SeIZE2xJcNLAtaHKVmSQWxJZPU3ShJLWygWdDiKIdB0EFIche3VJIqM+7T96JM8036fpNUkgPLv0DyC5Q/w2T6K4yl0SLKiDKijBZRRpQRZUQZLaKMKCPKaBFlRBlRRpTRIsqIMqKMFlH+8yg3tyT/9Rj3a7FnxZvHob6jVhv/frVVevOFKQtBmjyK3U7siSR/PUuza5FRNtNs7LYi/MzeuO3Zv5llnjmUCbqJacv/gUbSbBH+tYFyRInes1cEFVkFkSSFmcHeOqmTESV2FLNijBWNJN41bNivh8C9hgI9itUSKDsyytJHeaORpM1KvctcNtA4s0dsTn89K6+0m2GsoZBUkWJ9OB9hJPhEknWmERjLJFuGJR9R5kugLAgkywSzerWufrZSm0ArRAtmL+7E8nC97Y4IEJJZlKgdd/x6SwQFpSa56cyG3jUiE9C1PCNJV3uHZSn5VRggGB/9Y6u0MeuFgNRJfz4nyzTfFqVfARdMYiApNEuuqkld7d2OYViWycf4+VvUDkRdao15Bp+tFyiUtgUmzebJHbidVt0HU6+n/mzc2ndBzUsiyFRvdi8RKJ+W/hWoe1E+JKiZDCap4+PRTokJJZolHqR+4Nmz82SBLe0zIkrvGcd+5JaB2hnifOPeIUgX9UeUsEV5hQQpPXBnhfLK4IVvgogyZ4+f6AozA5/mTyyrKlsKIxtvVnJwXUpvS96gecxlVkOam2QIivKpEAIgUCSPHzgsoA3ovYirUl/N7qxadqAZibu8hkfHe5SXyTYElBkCpRt07xZQwBsmmz9VfqhEVZ2NcwnKUtyh7BDLjiCidA7eJDexU7WkDZxk5T72nmWJmVzrAsHSCyyZWXbgKHsiyrlOTKm0gKAkp5XmjiX4eUOvoXmOYZnWYmcNi1IWRJRznRjJEe+PT89Zwp/c1PTuXE6U3D4EsmIbh/JOfQiBcmhBJO6GVD3mTfxTlohn4CYzLyHGOuP6r6J8mqQDZ7b3OmSH+k7jCcsShUBNynFMHTJLR6McwkTX2DCBQ3n/6hjx7dADS8x7mVU2vhkbnKNAf81FQLmEbUgkH1iiKkKT6Q4kRq3kV6KsaCTvWaJMTJ8trKf/RpQjCfR3vx5Ljm2hnF465r8WpSNB2B9yYkSNkvTkxe7096I0JEg7bSzLMn2zvR1leirgr2SesHw7yVSW5eHdO9nOC8zs95M0L0bS325/RRfj/wPlj7GIMqKMKCPKaBFlRBlRRpTRIsqIMqKMFlFGlBFlRBktoowoI8poEeXbUf4TyhY/AuU/oWzxI1D+BGWLfk9t4cw5x1c3ycMSKH+CskVLPL1pb8tAP5CBKi/2C6D8CcoWRiylwn7+yt+fjGpFZHs9r0kol1O2IJGsdaVZdThzOAl9+HNyGRy8F7idXmpfFRdBl7IQkhhli0V0KexpoLZsDxxn1sM14zYY1Qg8Uq30wdpBN4GFkETMykV0KaZzVRMMy+GixkewBUeq8+UYvgWUfU0SpWyxhC7FRFKdoHsrgCyHu9mYhTtvCt3QH9ikyMe6ueSKn5VkZYsFdClmJD9MJRykD8U4mYQrZ00gO63nJTWVxKEkK1ssoEsxI6kvLb0K8qBJWU8x0xRZS4gE3eZyuQhAkGOfz0mCssXEEq1LMSepSyB0SUXw3lYx1ajnXbcZwyVkc2zuqnKHHEBPZ77efXyWU7HPvRuvbPHMQYAqAHckh3C1SmBlJdmz4pEc5hpW3SQrDn6W+0mQYC/iJFbZ4glLqJzCf/ckM2iJzuZhxLl6bGEQna4BfDHcwNrpWrUvyzHYq2ccnLLFLClDCnxw9+Grx2pKHtiC+sXNenJHoXy7gaj58WHh6od2eDae9W7Ozs2vISgXULaY3kQgdSn0uKtHkg2gsMSgbDwhRKEK7wQkHdoMA9btKE2mtQow/UkadzsGoFxC2WLqPE5LwZDI70mWauEJLYcwDu6r9zEVnnYgIb5cD4JPVJRHyP65QsfnKCnKFmPn8SzV9Fk9qGIkweUQUse3uhhdYq+gSniRj2z4NCvyNE8+qxT61MEpyhbTxEDqUphpmZD0RXZzVx6cstbJJrz+To4rjlU3el7s/Ljs0JUt/OdwrC6FmQfi0qG1HLY+Nbln+p2MQOq/tqN3Dc9Qq+C8kq5sYY2mS7EyCUOCrQeVY5jMtQBAYm5uRyBZzp6hQlJ0urLFlODidSm4kWrl6HrQxKXjp4btbtx0BDUQLwtin09r9llwoClbuKcugi7FwLCQ9mkRVcWolGeu948MmJdV6m72/PxVC+zV9TQVAaIuhVr/irIi1CirSOV9o8MLZEtr91zQv9QpYy+XX5KKAFWXYozZTYq00wCvMO9ZpX7Jg7snO/3EvEm+qJdmr9YMmh4DWZdiZIlVxrAvb9mH0GGKIWt0d3oB+zwL+grlEsoWVF2KsQ941fS6oSt9di4L6mWKQbmEsoUXefHRjirokPeJELeSsKmAf5EFfYlyAWULuslz9QMEHTYmj0rRKBdQtljCqp8g6BCSR73eM3ROf4CdfkAf/gS4RtxfGfhE/7VrRJSLWUQZUUaUEWW0iDKijCgjymgRZUQZUUaLKCPKiDKijBZRRpQRZbSIMqIMMHlZ5RHlEqa+oy7yX49Syh9B8kewJKA8HXUV6EeFraHf54uRRLPc5+9HmXtFqQVKdKV1w09qOkmzRfg9fSCiVBtgM5H0fdmpn9Y5YhR2KlWEGnyvjLJAkVygDyNK3RJ4mqs+dG4jb42J+61zy4qgZ+AX995wJMl9GBtjqqUWSqKdb4CVHdi/WnugdlGh683uyqQFiiS5D1NjjOk6XRjLfPz9/GZcewvcmWs8y56vuma4cfjebWpj+/wv90F6jTHMCliMpQ+V23QvQBVvLkbpcXRmU2xNIFmWrgY+/6t9WLkrhsZKNtaHhveCT5sNR1WKHKJK0brPU571kdrKlhpOkvW2rrkD1yAs0YeVu0KRTFkLr4SoJrmeU8EmJSAJmQ/N8HkmRh3dqeU1fE6WKRfZTN8jcBRL9EFJ9ugrVjq82X3z63MfzpI9Kx0Zbuoe4llD19Vnd4N/HRt4rbP1bqfv0QCXnyX6sFJ3cquvGCbXoWUuk7gE39HNg5SH5BpwEuwValbrk+c7d7Z3CYtVY5ws3d0F6Xss0QdNUt+T2kSJGcqsDwyVAzp53XsefwbU/1emqKkypepmOGXq4v4KtuLYxDzvYKk6vQ+msNeEx4N2ixElDy7qMCiF9+vC/A+RAlhWdsU1Mero8IiQYkh/7R57VYBUSsh9UDP52Lo+tNOsLMQHUODDV6Uo1J0FaKXYlLgbhlA0uu/ZccTTfO2gT/Q9hiADWzeofXBRYSSZMbiigpE9+jPd/412KkiSXplczi6bOngzwCgepAv4qLFV/60+pGPacLRtMYQ2hUkhx1eVslAxloNydBur9DAS7w6HjcIVWK8v1+RWpV4uBMllaH0YpQtGlgyhTTFPIU+NfuCpYNJYehxy0tzZgkZR+/okK+URmKdoWh+8Km/DEnNWOR99OV+rGKs0MXKoKoW7j0njjaMJvRs+y0JukBo8tD5s71iiSlvHB27labutcXJonXLlJGPMOJQoQRM+r+uMjV5d9tg3O7Q+3LFkGEWF4SOt/qI8n12fSsw4GuMphZbQaSARQmmC2CcUCxXzjozWhzlLhqIw+BfzHhPVmARuHJXJ5QroKGbLD+FtI60PM5ZsWEUQRcJbKxaj7HxkGA52HOsCtm5+EvnR721pffBZsiOuB/rhgonb7bYjKDJUngog4l60dJLUPngsWYbUYjAyb9koZkQcB2pWbxcguVwfGF4CQNZds9vtui2nj6PBbU3YLkByqT5kb9/oUhFG4cZR/4A+qC8k3m0FYRRGgb9+fx/0FxLvJnkljWKI2Vy+vQ9bnU++HSWnjeJn9GGr3pm/38H792+GW6YPcdfvYhZRRpQRZUQZLaKMKCPKiDJaRBlRRpTRIsqIMqKMKKNFlBFlRBkNiXK/FvsIaQmU1Q/RUvj9KFtsebAxeViih/Iodrvf4BuvUOawA3sfLi+WGP+myLLf4RuvUO4z0iFMQhU9njkn9W86zZgdfjHKsdJojYKQZXsuqNOpyEjiGJMd3oqyw2sppLqcsiM7Jr+rKkFbpba7yTUU6FGslp2VCJTny3GJEFf6KG8UkllWq5plGMs9ZMchC5kSV7Bzj4UgyZXEspudtA6e0xNJ1hmBDRjLBLJ78xVK6UI+lMZMaSWr5EIoocGydWfLDyQ3bm8viOX9KdKH6213ROWVf5BH/20ul4ugyNaYOXW9JYKAsjXlNJakbDISS8mvgr300ddPO1t0KuRy0ppzkw7x9e6jAkzvzYwiAmVrCpN1nKocSbZjGJblWNn74mTNL57BN9hDFBunVnOYPTgFJ5kbltFQtrYw+WiK4BtPjQHBMuSIUvZ1coiclEoJqmbWH1pgqJgPAIFSk9Tx8egXhBhhCyzL12e9PqCUZ890Gb337+AFhA9ZUD9MQm7G4p1VHpYN7DMaymHFbFzFu5zWT6cRAioEWAUemvuA8uJ3fnoMB86qzZBP6mJ7tXKtVU7Rn6S5xUHla4KIUgmKVIakt+LANEKc2breLw9aZU+i4wIo0zy16iSJ90pEvj6r3LPdM5Sg19Tu5OHV7L6oZQca/UVgnRV7OSF0xfxlsg3s9VjDp/ZyW8oXNo6nKDPQp6/Hs8W32PlgEQVW/7EnAOYoKW/K5LjiNAFnlX/l4CCUY+XiPUsoSe2m6+Jrls+cphY7a1SU09ptzipfAeMTHuW00tyxBOd2egXM869ZflMydE8y6Kxyn0RBnZWn5yzhWXJlNHQMy+T2IeR7UHpZEIOuwMRY+ZQl4nmjsS8hXH75aUnl96JUmUfPz2nYWeVfJukplSWCZJ45rRrTo06+x8HX7lmxh+cgqSk3V4ZD+cgS8wy8mt6M9a+Xze9FudMaiBtAFrSo3bHsMW0kU88TrQn4PpQClgV9J0uUiemLAPFalep7UXYuC3pXea3HkmNbKKfXt/n7UHJwFvRtLNHx5eQNJ30fynSjF74iTd/N8i9E6i9QyrIkfoEMzoK+heXfWPO+fVPgn7es3Xcs/0oPvn9/5fbNJAeWffpvoPy/sYgyoowoI8poEWVEGVFGlNEiyogyoowWUUaUEWVEGS2ijCgjymgR5f8BSnlZ0UpK+bXkEWVqvicnlee2hPN+/imUZtMigeWeIqTgHIO6o4nnJJT7fDGSFJYFsjrY2emoq0A/KkLhvjofPMejbN3wk5pOUh3GiB4FZXtV7pVioFVTdB+KI0M20LqpVBG2BHmbpLE7tEqSvofajJyJpO/LTv20zpEky7pwp4uC/at1l1WEU5j87eZYMQeSvocaRee2l9XI9c8cuT5czrYYlu447KIinA0227iPlYWi6Hu0883IssPEmdUYW1iRsQbK0ni3rd9YMxxL37tNfW6PmBMXvBZSPo46vxnX3sKbmUiqapocmtu5OLk1J2yfUCz9k+dLV4cPZzlWjcG3gU7H9FZO4kRA91l7JAumS98TyBrauoigvPsjtcovNZwk6+0p52PAQ7C0vtGBL+TTqr92y17OYIHCJ5mZbZw65J0Bc1KdXFzZs15tfSmiwKlMuVCfP2UkCJYJ8uTAKmPuw06FKxFLQKecz0gOKNlertxhwsHerdSP1FzwZCkgLK13M2mKcxvS8vMHMydVbXL3dKrukSQzv5TwGHa5vps6RnXOv0pYvBzjpFv7vE7Apxeu9GDzoHIjue5KgiQ5QxkUcs0p0CYkOKRl6taeFYikS8zzjpKq41BqOYpUXvfeyM6qaOsWHqWZnPlYNv1VBsaYzMhSMOPoZjqb5kRI1Zi/do8DKyCFSvvdbl7+z3aTXSEohdcHoVCK8BAjbbnAWJJr1j/1zzI4XptrBoxFkxlhpBxwwnbypMxdcsjaxRfQ9zAomRedC5VQNYAn0D96Wo5DZ5mrHA9/H1DZa6TNZbZTnAhZSbv7QfNRWyo02t7re6zLyXhwWLjqJYuPwVMFF1CSXgxDmHyMYXIyGy81yiT1NACCchI3p9aXa3KrfDDwdYug72FSyPFVpSyy3s3VUKuH3/YEnVCZhGZpJqXOBLegE7brO31PxHnpeceoKOcpZN7oB54KlkLoHOjWOJSYnCyt7IROGo9lcJ7ssyzkBndeunRqXEiUfPTlfC2GCKM0MXLgxOrNVDIhi6FIurWnVE6uGlPqxoAnjjpjo1eXPfHkeWxJ6/jArSLObmucHPY2npu3gyvaqfWVlTndqBuTQ88q59qlTA8E8eR5tATfqL84THA3phI6s0VqpXw6/Hc7Zu3RC0CBOvV9ltJQvtXAFloPcYbtZ+ES+tyqYlPFj2btJnzjWI2qULj3CZtlSBJq1rdWuEfZ+cgQoxhzlw/al7eVp2iM+JarXYYkpfxfpzJM3G63HfIb4O30AoP0PXhFIempe9FK7ylKCkZyLxuFpQijuNK2FFQEklMviCIGNFEKaeSMui12s4wbRUncnVERSLpeUOUg6BJ8NOM6TJTkjS4FgaRmycjCGrkQb95+lauHcSrKK4nk0Aku03/AcjUfiCg5jeQ/ZVQH7yPJpVBGiygjyogyoowWUUaUEWW0iDKijCgjymgRZUQZUUaUEUFEGVFGlNEiyogyonywfv/e8cnDIq0cxW4n9m9FqQ7pxmtsyDUVRF4scSs3QQof7LtJqs2cSJiqkPJggByRHRBqO9GZ0zbC5ONuN3agoTxQSNa66K46nHmOIGk6r3YW41iqnVlcEIWM0iLsMOivUVZqqxnK1VqzS81WMLIDgqS6zBTpoFiusqzLqCT5XW0MGqXa9leProYgmbpSPFgL0tb8ZazIMhzL8+VIFDHSVvoob2CUfCLJOiNuAWU5kRzmhrgVsBY0yc5V+CQFnOUkIZRcSSw7H2UDRdm6flfq1PcqQ8yqGckPwya8BTMnhx/+M+VBOZjl/KDXSi6EsgCibF2/NcnxwHAIyxlJXcReWlkCQJysDZDGBSwIy83lchFk8R1+vSWCgLI1hcm6/9UY/ncMwHJOUh/tCdDnMN6tCpIKezKonWMwH3fVzTXnJh3i690HJDPbPJ74DkPZ2sLko97g7KQxbkrSJ5TlHckhXK2S8ONSbZx0xX+1LQ8C+3jjNHcOfpYbXlq7eXKwNgilJqmHcfQLQjTKQJb/3ZPMQEfPcismkVsxCbP21NB4qSSZhhtYM6uS0QJ18J4dqw1CKZmKTZUVXBkVkSzKsHIG7oLi6rEvIXNim7nCSf2B+gf391GWgS7Khzb64QO5uQPeqe9hmhD7DIbyfwIMAOAAf9SdKkjLAAAAAElFTkSuQmCC';

interface ImageWrapperFuctionProps {
  turnOnGradient: boolean;
  styles: string;
  children: React.ReactNode;
}

export const ImageWrapperFuction: React.FC<ImageWrapperFuctionProps> = ({ turnOnGradient = false, styles, children }) => {
  return turnOnGradient ? <CpdsImagWrapper styles={styles}> {children}</CpdsImagWrapper> : children;
};

const CpdsImage: React.FC<CpdsImageProps> = (props) => {
  const {
    domRef,
    exClasses,
    styleOverrides,
    children,
    src,
    alt,
    srcSet,
    sizes,
    loading = 'eager',
    imageArray,
    aspectRatio,
    objectFit,
    borderRadius,
    placeholderSrc = placeholderSrcDefault,
    usePlaceholder = true,
    turnOnGradient = false,
    tinyImageLoader = false,
  } = props;

  const isTinyImageActive = tinyImageLoader && src?.indexOf('cdn.sanity.io/images') > -1;

  const getImageSrc = (src: string) => {
    if (checkValue(src) && isTinyImageActive) {
      return src.includes('?') ? `${src}&w=25&h=25` : `${src}?w=25&h=25`;
    } else {
      return src;
    }
  };

  const cpdsImageSrc = getImageSrc(src);

  const [imageSrc, setImageSrc] = useState(cpdsImageSrc || placeholderSrc);

  useEffect(() => {
    if (cpdsImageSrc) {
      setImageSrc(cpdsImageSrc);
    } else {
      usePlaceholder && setImageSrc(placeholderSrc);
    }
  }, [cpdsImageSrc, placeholderSrc, usePlaceholder]);

  const handleError = (e) => {
    // if (onError) onError();
    usePlaceholder && setImageSrc(placeholderSrc);
  };

  const onImageLoad = (e) => {
    if (isTinyImageActive) {
      imageLoad(e.currentTarget, src);
    }
  };
  let styles = '';

  styles += turnOnGradient ? `mask-image: linear-gradient(rgba(0, 0, 0, 0.99) 0%,rgba(0, 0, 0, 0.99) 100%);` : '';

  if (checkValue(aspectRatio)) {
    if (aspectRatioMap[aspectRatio as CpdsAspectRatioTypes]) {
      styles += `aspect-ratio: ${aspectRatioMap[aspectRatio as CpdsAspectRatioTypes]};`;
    } else if (aspectRatio.includes('[') && aspectRatio.includes(']')) {
      const extractedAspectRatio = extractAspectRatio(aspectRatio);
      if (extractedAspectRatio) {
        styles += `aspect-ratio: ${extractedAspectRatio};`;
      }
    }
  }

  if (borderRadius) {
    if (typeof borderRadius === 'string') {
      if (borderRadiusMap[borderRadius as CpdsBorderRadiusSizesTypes]) {
        styles += `border-radius: ${borderRadiusMap[borderRadius as CpdsBorderRadiusSizesTypes]};`;
      } else if (borderRadius.includes('[') && borderRadius.includes(']')) {
        const extractedBorderRadius = extractBorderRadius(borderRadius);
        if (extractedBorderRadius) {
          styles += `border-radius: ${extractedBorderRadius};`;
        }
      }
    } else {
      const { topLeft, topRight, bottomRight, bottomLeft } = borderRadius;

      if (topLeft) {
        if (borderRadiusMap[topLeft as CpdsBorderRadiusSizesTypes | CpdsUnitType]) {
          styles += `border-top-left-radius: ${borderRadiusMap[topLeft as CpdsBorderRadiusSizesTypes | CpdsUnitType]};`;
        } else if (topLeft.includes('[') && topLeft.includes(']')) {
          const extractedTopLeft = extractBorderRadius(topLeft);
          if (extractedTopLeft) {
            styles += `border-top-left-radius: ${extractedTopLeft};`;
          }
        }
      }

      if (topRight) {
        if (borderRadiusMap[topRight as CpdsBorderRadiusSizesTypes | CpdsUnitType]) {
          styles += `border-top-right-radius: ${
            borderRadiusMap[topRight as CpdsBorderRadiusSizesTypes | CpdsUnitType]
          };`;
        } else if (topRight.includes('[') && topRight.includes(']')) {
          const extractedTopRight = extractBorderRadius(topRight);
          if (extractedTopRight) {
            styles += `border-top-right-radius: ${extractedTopRight};`;
          }
        }
      }

      if (bottomRight) {
        if (borderRadiusMap[bottomRight as CpdsBorderRadiusSizesTypes | CpdsUnitType]) {
          styles += `border-bottom-right-radius: ${
            borderRadiusMap[bottomRight as CpdsBorderRadiusSizesTypes | CpdsUnitType]
          };`;
        } else if (bottomRight.includes('[') && bottomRight.includes(']')) {
          const extractedBottomRight = extractBorderRadius(bottomRight);
          if (extractedBottomRight) {
            styles += `border-bottom-right-radius: ${extractedBottomRight};`;
          }
        }
      }

      if (bottomLeft) {
        if (borderRadiusMap[bottomLeft as CpdsBorderRadiusSizesTypes | CpdsUnitType]) {
          styles += `border-bottom-left-radius: ${
            borderRadiusMap[bottomLeft as CpdsBorderRadiusSizesTypes | CpdsUnitType]
          };`;
        } else if (bottomLeft.includes('[') && bottomLeft.includes(']')) {
          const extractedBottomLeft = extractBorderRadius(bottomLeft);
          if (extractedBottomLeft) {
            styles += `border-bottom-left-radius: ${extractedBottomLeft};`;
          }
        }
      }
    }
  }

  if (checkValue(objectFit)) {
    styles += `object-fit: ${objectFitMap[objectFit]};`;
  }

  if (imageSrc === placeholderSrc) {
    styles += `object-fit: cover;`;
  }

  const generateSrcSetFromArray = (imageArray: CpdsImageObjectType[]) => {
    let srcSet = '';
    imageArray.forEach((image, index) => {
      if (deviceSizes[image.name]) {
        srcSet += `${image.url} ${deviceSizes[image.name]}w`;
        if (index !== imageArray.length - 1) {
          srcSet += ', ';
        }
      }
    });
    return srcSet;
  };

  const getReleventStyles = (releventStyleTags: string[], styles: string) => {
    if (!styles) return ''; 
    
    const stylesArr = styles.split(";"); 
    
    const releventStyleArr = stylesArr.filter(style => 
      releventStyleTags.some(releventStyle => style.includes(releventStyle))
    );

    return releventStyleArr.length ? releventStyleArr.join(";") : '';
};

  const srcSetData = checkValue(imageArray, true)
    ? checkValue(srcSet)
      ? srcSet
      : generateSrcSetFromArray(imageArray)
    : srcSet;

  /* Important : By default html tag is <div></div>
  but you can change that into suitable html element base on your requirements*/
  return (
    <ImageWrapperFuction turnOnGradient={turnOnGradient} styles={getReleventStyles(["border-"], styles)}>
      <CpdsImageStyleTag
        className={`cpds-image ${exClasses}`}
        ref={domRef}
        styleOverrides={styleOverrides}
        styles={styles}
        loading={loading}
        src={imageSrc}
        alt={alt}
        srcSet={srcSetData}
        sizes={sizes}
        onError={handleError}
        onLoad={(e) => {
          onImageLoad(e);
        }}
      />
    </ImageWrapperFuction>
  );
};

export default CpdsImage;
